import { Box, TableCell, TableRow } from '@mui/material';
import classNames from 'classnames';
import { BaseItem, Column, InternalData } from '../VirtualizedTable.types';
import { useTableScrollerContext } from '../scroller/useTableScroller';
import { CHECKBOX_COLUMN_WIDTH, COLUMN_GAP, useStyles } from '../virtualizedTable.styles';
import { getFirstColumnIndentShift, getFirstColumnPaddingLeft } from '../virtualizedTable.utils';

interface StickyGroupRowProps<T extends BaseItem> {
  row: InternalData<T>;
  position: number;
  columns: Column<T>[];
  hasRowCheckbox: boolean;
  hasRowActions: boolean;
  hasHideColumnsButton: boolean;
}

const StickyGroupRow = <T extends BaseItem>({
  row,
  position,
  columns,
  hasRowCheckbox,
  hasRowActions,
  hasHideColumnsButton,
}: StickyGroupRowProps<T>) => {
  const { classes } = useStyles();
  const { isScrolling } = useTableScrollerContext();
  const indentShift = getFirstColumnIndentShift({ row, hasRowCheckbox });

  return (
    <TableRow className={classes.stickyGroupRow}>
      {hasRowCheckbox && (
        <TableCell
          padding="none"
          className={classNames(classes.checkbox, classes.stickyGroupCell, {
            [classes.stickyCheckbox]: columns.some((c) => c.fixedHorizontalScroll),
          })}
        />
      )}
      {columns.map((column, index) => {
        const isFirstColumn = index === 0;
        const component = column.render(row, position);
        const paddingLeft = isFirstColumn
          ? getFirstColumnPaddingLeft({ row, hasRowCheckbox })
          : COLUMN_GAP;

        return (
          <TableCell
            key={column.name}
            className={classNames(classes.stickyGroupCell, {
              [classes.tableCellClickable]: !!component.onClick,
              [classes.fixedCell]: column.fixedHorizontalScroll,
              [classes.fixedScrolledCell]: column.fixedHorizontalScroll && isScrolling,
              [classes.fixedRightCell]: column.fixedRight,
              [classes.fixedRightScrolledCell]: column.fixedRight && isScrolling,
            })}
            onClick={component.onClick}
            sx={(theme) => ({
              width: column.width ?? 'auto',
              minWidth: column.width ?? 'auto',
              maxWidth: column.width ?? 'auto',
              ...(column.fixedHorizontalScroll &&
                hasRowCheckbox && { left: CHECKBOX_COLUMN_WIDTH }),
              ...(component.onClick && { cursor: 'pointer' }),
              ...(isFirstColumn &&
                indentShift > 0 && {
                  position: 'relative',
                  transform: `translateX(${theme.spacing(indentShift)})`,
                  '& > *': { maxWidth: `calc(100% - ${theme.spacing(indentShift)})` },
                }),
            })}
          >
            <Box
              className={classes.cellContent}
              sx={(theme) => ({ paddingLeft: theme.spacing(paddingLeft) })}
            >
              {component.node}
            </Box>
          </TableCell>
        );
      })}
      {hasRowActions && (
        <TableCell
          padding="checkbox"
          className={classNames(classes.stickyGroupCell, classes.actionsColumn)}
        />
      )}
      {!hasRowActions && hasHideColumnsButton && (
        <TableCell className={classNames(classes.stickyGroupCell, classes.emptyFixedColumn)} />
      )}
    </TableRow>
  );
};

export default StickyGroupRow;
